import React from 'react';
import { useDispatch } from 'react-redux';
import { showSuccessBanner } from '@utils/application';
import { Body5, Container } from '@ui';
import { TransparentFlatButton } from '@ui/buttons';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { ImproverForm } from './ImproverForm';
import { useEditableAgentScripts } from './useEditableAgentScripts';
import { useImproveAgentScript } from './useImproveAgentScript';
import { openAgentScriptInFlowEditor } from './openAgentScriptInFlowEditor';

/**
 * Improve-an-existing-script panel: pick an owned script, describe changes, and
 * rewrite its plan in place. The prior plan is snapshotted client-side so the
 * user can revert until they leave the page.
 */
export const ImproveAgentScriptPanel = () => {
  const dispatch = useDispatch();

  const { scripts, isLoading, reload } = useEditableAgentScripts();
  const { improve, revert, isImproving, isReverting, error, descriptionError, notFound, canRevert } =
    useImproveAgentScript();

  const [selectedId, setSelectedId] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [improvedScript, setImprovedScript] = React.useState(null);

  // A 404 means the script was deleted elsewhere — refresh the selector.
  React.useEffect(() => {
    if (notFound) {
      reload();
      setSelectedId('');
      setImprovedScript(null);
    }
  }, [notFound, reload]);

  const selectedScript = scripts.find((script) => String(script.id) === String(selectedId)) || null;

  const handleSubmit = async () => {
    if (!selectedScript) return;
    const record = await improve({
      id: selectedScript.id,
      scriptName: selectedScript.script_name,
      description,
      currentDefinition: selectedScript.definition_json,
    });
    if (record) {
      showSuccessBanner(`"${record.script_name}" was improved.`, null, dispatch);
      setImprovedScript(record);
    }
  };

  const handleRevert = async () => {
    const ok = await revert();
    if (ok) {
      showSuccessBanner('Reverted to the previous version.', null, dispatch);
      setImprovedScript(null);
    }
  };

  return (
    <Container>
      <Container bottom={24}>
        <Body5 isLowContrast>Pick one of your scripts and describe how to improve it.</Body5>
      </Container>

      <ImproverForm
        scripts={scripts}
        isLoadingScripts={isLoading}
        selectedId={selectedId}
        description={description}
        descriptionError={descriptionError}
        error={error}
        isImproving={isImproving}
        onSelectScript={setSelectedId}
        onDescriptionChange={setDescription}
        onSubmit={handleSubmit}
      />

      {improvedScript && (
        <Container top={32}>
          <Body5 bold>"{improvedScript.script_name}" was improved.</Body5>
          <Container top={16} row gap={16}>
            <SkeletonButton onClick={() => openAgentScriptInFlowEditor(improvedScript.script_name)}>
              Open in Flow Editor
            </SkeletonButton>
            {canRevert && (
              <TransparentFlatButton disabled={isReverting} onClick={handleRevert}>
                {isReverting ? 'Reverting...' : 'Revert'}
              </TransparentFlatButton>
            )}
          </Container>
        </Container>
      )}
    </Container>
  );
};
